import { createContext, useContext, type FC, type PropsWithChildren } from 'react';
import { type QueryClient } from '@tanstack/react-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';

import {
  FormsSettingsSchema,
  type FormsSettings,
} from '#apps/client-web/features/settings/models/app-settings';
import { type AppSettingsService } from '#apps/client-web/features/settings/services/app-settings';
import { useLoadAppSettingsQuery } from '#apps/client-web/features/settings/hooks/use-load-app-settings-query';

const FormSettingsContext = createContext<FormsSettings>(
  FormsSettingsSchema.decode({}),
);

export const useFormSettingsContext = () => useContext(FormSettingsContext);

export type FormSettingsProviderProps = {
  appSettingsService: AppSettingsService;
  queryClient: QueryClient;
};

export const FormSettingsProvider: FC<
  PropsWithChildren<FormSettingsProviderProps & LoggingContext>
> = ({ appSettingsService, queryClient, children }) => {
  const loadAppSettingsQuery = useLoadAppSettingsQuery({
    appSettingsService,
    queryClient,
  });

  const formsSettings =
    loadAppSettingsQuery.data?.forms ?? FormsSettingsSchema.decode({});

  return (
    <FormSettingsContext.Provider value={formsSettings}>
      {children}
    </FormSettingsContext.Provider>
  );
};
